<template>
    <div class="filters-pane">
        <div class="filters-pane__header">
            <CompassTooltip
                v-if="isSetup || isAllFav || (isSelect && activeFilters.length)"
                :content="$t.__('common.actions.back')"
                placement="bottom-start"
                :arrow="false"
            >
                <CompassIconButton icon="caret-left" size="small" @click="back" />
            </CompassTooltip>
            <h3 class="filters-pane__title typographies-heading-h3">
                {{ isAllFav && useFavorites ? $l10n.__('Favoris') : $l10n.__('Filtres') }}
            </h3>
            <CompassPlainButton
                v-if="isSetup"
                :label="$t.__('listing.FiltersPane.favorites')"
                size="small"
                @click.prevent="updateView('allFavoris')"
            />
        </div>

        <div v-if="!isSetup && !isAllFav && useFavorites" class="filters-pane__favorites">
            <div class="filters-pane__favorites-header">
                <h4 class="filters-pane__favorites-title typographies-paragraph-caps-small-bold">
                    {{ $l10n.__('Favoris') }}
                </h4>
                <CompassPlainButton
                    v-if="!isAllFav"
                    :label="$t.__('listing.FiltersPane.see_all')"
                    size="small"
                    @click.prevent="updateView('allFavoris')"
                />
            </div>
        </div>

        <ul
            v-if="!isSetup && favoriteShortcuts.length > 0 && useFavorites"
            class="filters-pane__favorites-list"
        >
            <FavoriteItem
                v-for="shortcut in favoriteShortcuts"
                :key="shortcut.id"
                :favorite="shortcut"
                :listinghash="listinghash"
            />
        </ul>

        <div class="filters-pane__content" :class="{ 'filters-pane__content--no-border': isSetup }">
            <CompassInput
                v-if="isSelect"
                ref="searchfilter"
                v-model="searchword"
                class="filters-pane__input"
                prefixIcon="search"
                :placeholder="$t.__('listing.FiltersPane.search_filter')"
            />

            <!-- NEW FILTERS -->
            <div v-if="isSelect" class="filters-pane__list">
                <!-- NATIVE FILTER LIST -->
                <template v-if="nativeFilters.length > 0">
                    <h4 class="filters-pane__list-title typographies-paragraph-caps-small-bold">
                        {{ $l10n.__('Réguliers') }}
                    </h4>
                    <FiltersPaneList :items="nativeFilters" @change="setupFilter" />

                    <div
                        v-for="category in customFilters"
                        :key="category.title"
                        class="filters-pane__list--custom"
                    >
                        <h4 class="filters-pane__list-title typographies-paragraph-caps-small-bold">
                            {{ $l10n.__('Champs personnalisés %s', category.title) }}
                        </h4>
                        <FiltersPaneList
                            :items="category.filters"
                            :isCustom="true"
                            @change="setupFilter"
                        />
                    </div>
                </template>

                <p
                    v-if="!nativeFilters.length && !customFilters.length"
                    class="filters-pane__list--empty typographies-paragraph-medium-regular"
                >
                    {{ $l10n.__('Aucun filtre trouvé') }}
                </p>
            </div>

            <ListingFilter v-if="isSetup" :filter="currentFilter" @filter:apply="applyFilter" />

            <div v-if="isSummary" class="filters-pane__active-filters">
                <div v-if="activeFilters.length" class="filters-pane__active-filters-header">
                    <h4
                        class="filters-pane__active-filters-title typographies-paragraph-caps-small-bold"
                    >
                        {{ $l10n.__('Résumé des filtres') }}
                    </h4>
                    <CompassButton
                        :label="$t.__('common.actions.reset')"
                        type="secondary"
                        size="small"
                        leftIcon="revert"
                        class="pull-right"
                        @click.prevent="resetFilters()"
                    />
                </div>

                <ul class="filters-pane__active-filters-list">
                    <li
                        v-for="activeFilter in activeFilters"
                        :key="activeFilter.name"
                        @click="setupFilter(activeFilter)"
                    >
                        <CompassTagInteractive
                            color="blue"
                            :modelValue="activeFilter.name"
                            :keyLabel="activeFilter.label"
                            :valueLabel="$filters.shortText(activeFilter.translation)"
                            @tag-remove="removeFilter(activeFilter)"
                        />
                    </li>
                </ul>
                <!-- PENDIND AND ACTIVE FILTER BLOCK -->
                <div class="filters_summary fs-3">
                    <!-- ACTIVE FILTERS -->

                    <!-- APPLY FILTER -->
                    <div class="mt-4 acenter">
                        <!-- ADD FILTER -->
                        <CompassButton
                            :label="$t.__('listing.FiltersPane.add_filter')"
                            type="primary"
                            @click="updateView('select')"
                        />
                        <CompassButton
                            v-if="favoritesIsEditable && useFavorites"
                            :label="$t.__('listing.FiltersPane.save_as_favorite')"
                            type="secondary"
                            size="small"
                            leftIcon="star"
                            class="mt-3"
                            @click="$store.commit(`${listinghash}/showDisplay`, 'favoritesForm')"
                        />
                    </div>

                    <!-- PENDING FILTERS -->
                    <!-- TODO -->
                    <div v-if="!!pendingFilters.length">
                        <p class="mt-3 mb-0 fs-3 semibold clearfix">
                            {{ $l10n.__('En attente') }}
                        </p>
                        <ul class="graylist fs-3">
                            <li
                                v-for="pendingFilter in pendingFilters"
                                :key="pendingFilter.name + '-pending'"
                                class="flex-level mb-1 regular"
                            >
                                <span class="pointer flex" @click="setupFilter(pendingFilter)">
                                    {{ $filters.shortText(pendingFilter.translation) }}
                                </span>
                                <CompassIconButton
                                    v-if="filterEditable(pendingFilter)"
                                    icon="close"
                                    variant="on-color"
                                    @click.prevent="
                                        $store.dispatch(
                                            `${listinghash}/removeFilter`,
                                            pendingFilter,
                                        )
                                    "
                                />
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <template v-if="isAllFav && useFavorites">
                <CompassTabs v-model="activeTab">
                    <CompassTabPane name="all" :label="$t.__('listing.FiltersPane.mine')">
                        <CompassInput
                            ref="searchfavorites"
                            v-model="searchwordFavoris"
                            class="filters-pane__input"
                            prefixIcon="search"
                            :placeholder="$t.__('common.actions.search')"
                        />
                        <div class="filters-pane__list">
                            <ul
                                v-if="privateFavorites.length"
                                class="filters-pane__shared-favorites-list"
                            >
                                <FavoriteItem
                                    v-for="favorite in privateFavorites"
                                    :key="favorite.id"
                                    v-model="favoriteid"
                                    :favorite="favorite"
                                    :listinghash="listinghash"
                                    :updateMode="display.favoritesUpdate"
                                />
                            </ul>
                            <CompassEmptyState
                                v-else
                                illustrationPath="/ressources/logged/img/illustrations/empty-custom-fields.svg"
                                :caption="
                                    !privateFavorites.length && searchwordFavoris.length
                                        ? $t.__('listing.filters.no_favorites_found')
                                        : $t.__('listing.filters.no_favorites_created')
                                "
                            >
                                <CompassButton
                                    :label="$t.__('listing.filters.back_to_favorites')"
                                    type="secondary"
                                    @click="back"
                                />
                            </CompassEmptyState>
                        </div>
                    </CompassTabPane>
                    <CompassTabPane
                        name="shared"
                        :label="$t.__('listing.FiltersPane.shared_with_me')"
                    >
                        <CompassInput
                            ref="searchfilter"
                            v-model="searchwordFavoris"
                            class="filters-pane__input"
                            prefixIcon="search"
                            :placeholder="$t.__('common.actions.search')"
                        />
                        <div class="filters-pane__list">
                            <ul
                                v-if="sharedFavorites.length"
                                class="filters-pane__shared-favorites-list"
                            >
                                <FavoriteItem
                                    v-for="favorite in sharedFavorites"
                                    :key="favorite.id"
                                    v-model="favoriteid"
                                    :favorite="favorite"
                                    :listinghash="listinghash"
                                    :updateMode="display.favoritesUpdate"
                                />
                            </ul>
                            <CompassEmptyState
                                v-else
                                illustrationPath="/ressources/logged/img/illustrations/empty-custom-fields.svg"
                                :caption="
                                    !sharedFavorites.length && searchwordFavoris.length
                                        ? $t.__('listing.filters.no_shared_favorites_found')
                                        : $t.__('listing.filters.no_shared_favorites_created')
                                "
                            >
                                <CompassButton
                                    :label="$t.__('listing.filters.back_to_favorites')"
                                    type="secondary"
                                    @click="back"
                                />
                            </CompassEmptyState>
                        </div>
                    </CompassTabPane>
                </CompassTabs>
            </template>
        </div>
    </div>
</template>

<script>
import ListingFilter from './filters/ListingFilter.vue';
import _ from 'lodash';
import { removeAccents } from 'Core/commons/Utilities';
import FavoriteItem from './favorites/FavoriteItem.vue';

import FiltersPaneList from 'vcomponents/listing/FiltersPaneList.vue';
import { ListingEvent } from 'js/types/events';

import { createNamespacedHelpers } from 'vuex';
const { mapState } = createNamespacedHelpers('user');

export default {
    components: {
        ListingFilter,
        FavoriteItem,
        FiltersPaneList,
    },

    props: {
        listinghash: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            prevFiltersView: '',
            currentFilter: false,
            searchword: '',
            searchwordFavoris: '',
            activeTab: 'all',
            favoriteid: '',
            loading: false,
        };
    },

    computed: {
        isAllFav() {
            return this.display.filtersView === 'allFavoris';
        },
        isSetup() {
            return this.display.filtersView === 'setup';
        },
        isSummary() {
            return this.display.filtersView === 'summary';
        },
        isSelect() {
            return this.display.filtersView === 'select';
        },
        filters() {
            return this.$store.state[this.listinghash].filters;
        },

        ...mapState({
            userId: 'id',
        }),

        display() {
            return this.$store.state[this.listinghash].display;
        },

        displayFilter() {
            return this.$store.state[this.listinghash].display.filters;
        },

        activeFilters() {
            return this.$store.getters[this.listinghash + '/activeFilters'];
        },

        pendingFilters() {
            return this.$store.getters[this.listinghash + '/pendingFilters'];
        },

        nativeFilters() {
            let filtered = Object.values(this.filters);

            if (this.searchword !== '') {
                var regexp = new RegExp(removeAccents(this.searchword), 'i');

                filtered = Object.values(this.filters).filter((filter) => {
                    let filterlabel = filter.label;

                    if (typeof filterlabel !== 'string') {
                        filterlabel = filter.label[0];
                    }

                    return (
                        regexp.test(removeAccents(filterlabel)) ||
                        regexp.test(removeAccents(filter.keywords))
                    );
                });
            }

            // Split native fields from customfields using filter on name pattern
            // Then order them alphabetically

            return filtered
                .filter((one) => {
                    return !one.customfield;
                })
                .sort(function (a, b) {
                    if (a.label < b.label) return -1;
                    if (a.label > b.label) return 1;
                    return 0;
                });
        },

        customFilters() {
            let filtered = Object.values(this.filters);

            if (this.searchword !== '') {
                var regexp = new RegExp(removeAccents(this.searchword), 'i');

                filtered = Object.values(this.filters).filter((filter) => {
                    let filterlabel = filter.label;
                    if (typeof filterlabel !== 'string') {
                        filterlabel = filter.label[0];
                    }
                    return (
                        regexp.test(removeAccents(filterlabel)) ||
                        regexp.test(removeAccents(filter.keywords))
                    );
                });
            }

            const categories = _.uniq(
                filtered
                    .map((one) => {
                        return one.customfieldcategory ? one.customfieldcategory : '';
                    })
                    .filter((one) => {
                        return one !== '';
                    }),
            );

            const categorizedFilters = [];
            for (const i in categories) {
                const filters = filtered
                    .filter((one) => {
                        return one.customfield && one.customfieldcategory === categories[i];
                    })
                    .sort(function (a, b) {
                        if (a.label < b.label) return -1;
                        if (a.label > b.label) return 1;
                        return 0;
                    });

                if (filters.length) {
                    categorizedFilters.push({
                        title: categories[i].toLowerCase(),
                        filters: filters,
                    });
                }
            }

            return categorizedFilters;
        },

        favoriteShortcuts() {
            return this.$store.getters[`${this.listinghash}/favoriteShortcuts`]
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        favorites() {
            return this.$store.state[this.listinghash].favorites;
        },

        privateFavorites() {
            const privateFav = this.favorites.filter((itm) => {
                return itm.ownerid === this.userId;
            });

            let favorites = Object.values(privateFav);

            if (this.searchwordFavoris !== '') {
                favorites = this.getFavoritesFiltered(privateFav);
            }

            return favorites;
        },

        sharedFavorites() {
            const sharedFav = this.favorites.filter((itm) => {
                return itm.ownerid !== this.userId;
            });

            let favorites = Object.values(sharedFav);

            if (this.searchwordFavoris !== '') {
                favorites = this.getFavoritesFiltered(sharedFav);
            }

            return favorites;
        },

        favoritesIsEditable() {
            return this.$store.state[this.listinghash].permissions.favorite;
        },

        useFavorites() {
            return this.$store.state[this.listinghash].useFavorites;
        },
    },

    watch: {
        displayFilter: function () {
            if (this.activeFilters.length === 0) {
                this.updateView('select');
            }
        },
    },

    created() {
        EventBus.on(
            'filters:edit',
            function (filter) {
                this.setupFilter(filter);
            }.bind(this),
        );
    },

    methods: {
        filterEditable(filter) {
            return !_.has(this.$store.state[this.listinghash].prefilter, filter.name);
        },

        getFavoritesFiltered(favorites) {
            var regexp = new RegExp(removeAccents(this.searchwordFavoris), 'i');

            return Object.values(favorites).filter((filter) => {
                let favoriteName = filter.name;

                if (typeof favoriteName !== 'string') {
                    favoriteName = filter.name[0];
                }

                return regexp.test(removeAccents(favoriteName));
            });
        },

        // Navigation
        updateView(view) {
            this.prevFiltersView = this.display.filtersView;

            this.$store.commit(`${this.listinghash}/updateFiltersView`, view);

            if (view === 'select') {
                this.searchword = '';
                this.$nextTick(() => this.$refs.searchfilter?.setFocus());
            }
        },

        back() {
            if (this.prevFiltersView === 'allFavoris' && this.isSetup) {
                this.prevFiltersView = 'select';
            }

            this.updateView(this.prevFiltersView);
        },

        setupFilter(filter) {
            if (!this.filterEditable(filter)) {
                return;
            }

            this.currentFilter = filter;
            this.updateView('setup');
        },

        applyFilter(payload) {
            this.$store.commit(`${this.listinghash}/applyFilter`, payload.filter);

            if (payload.addNew) {
                this.updateView('select');
            } else {
                this.applyFilters();
                this.updateView('summary');
            }
        },

        // Events to apply filter & update listing
        applyFilters() {
            EventBus.emit(ListingEvent.REFRESH);
        },
        resetFilters() {
            this.$store.dispatch(`${this.listinghash}/resetFilters`);
        },
        removeFilter(activeFilter) {
            this.$store.dispatch(`${this.listinghash}/removeFilter`, activeFilter);
            if (this.activeFilters.length === 0) {
                this.updateView('select');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.filters-pane {
    &__header {
        align-items: center;
        display: flex;
        padding: var(--spacing-3);
    }

    &__title {
        flex: 1;
        margin: 0;
    }

    &__favorites {
        padding: 0 var(--spacing-3) var(--spacing-1);

        &-header {
            align-items: center;
            display: flex;
            justify-content: space-between;
        }

        &-title {
            margin: 0;
        }

        &-list {
            border-bottom: 1px solid var(--colors-common-border-lighter);
            margin: var(--spacing-0) 0;
            padding: 0 var(--spacing-3) var(--spacing-1);
        }
    }

    &__content {
        padding: var(--spacing-3);

        &--no-border {
            border-top: none;
        }
    }

    &__input {
        width: 100%;
    }

    &__list {
        padding: var(--spacing-3) 0 0;

        &--empty {
            color: var(--colors-common-text-standard-caption);
            margin: 0;
        }

        &--custom {
            padding: var(--spacing-3) 0 0;
        }

        &-title {
            margin: 0 0 var(--spacing-1);
        }
    }

    &__active-filters {
        &-header {
            align-items: center;
            display: flex;
            justify-content: space-between;
            margin-bottom: var(--spacing-3);
        }

        &-title {
            margin: 0;
        }

        &-list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);
            margin: 0;

            &::v-deep {
                .compass-tag-interactive {
                    width: 100%;

                    .compass-icon-button {
                        margin-left: auto;
                    }
                }
            }
        }
    }

    &__shared-favorites-list {
        margin: 0;
    }

    &::v-deep {
        .compass-tabs__container {
            overflow: hidden;
            padding-top: var(--spacing-3);
        }

        .compass-tag-interactive {
            cursor: pointer;
        }
    }
}
</style>
